import React, {useEffect, useRef, useState} from "react";
import useSerializedRefresh from "../useSerializedRefresh";
import ApiService from "../../../services/ApiService";
import ProgressBar from "@ramonak/react-progress-bar";
import {notification, Spin, Typography} from "antd";
import TimeAgo from "./time-ago";
import {getCurrencyFormater, getDeviseSymbol} from "../../../i18n";
import {Trans} from "@lingui/react/macro";
import {t} from "@lingui/core/macro"
import {getPrimaryColor} from "../tokens";

const {Text,Title}=Typography

// Taille de page de /campagne/donors (unitPerPage côté API)
const DONORS_PER_PAGE=30
const donKey=(d)=>(d?.type || "") + ":" + String(d?._id || d?.id || "")

export default function DonSection({context,campagneData,campagneTarget,forceMobile,})
{
    const [loading,setLoading]=useState(true)
    const [dons,setDons]=useState([])
    const [stats,setStats]=useState({})
    const [hasMore,setHasMore]=useState(false)
    const [loadingMore,setLoadingMore]=useState(false)
    const nextPage=useRef(2)
    const loadingMoreRef=useRef(false)
    const scrollRef=useRef(null)
    const sentinelRef=useRef(null)
    const seenIds=useRef(new Set())
    const notificationsInitialized=useRef(false)

    const formater=getCurrencyFormater(context)

    let progressColor=getPrimaryColor(campagneData.config)
    const hideDonDate=campagneData.config?.hideDonDate || false

    const getDisplayName=(campagneExtraData={})=>{
        if(campagneExtraData?.displayName === "Anonyme"){
            return t`Anonyme`
        }
        return campagneExtraData?.displayName || t`Anonyme`
    }

    const showDonNotification=(_dons)=>{
        if(!Array.isArray(_dons)){ return }
        const unseen=_dons.filter(d=>{
            const key=String(d?._id || d?.id || "")
            return key && !seenIds.current.has(key)
        })
        _dons.forEach(d=>seenIds.current.add(String(d?._id || d?.id || "")))

        if(!notificationsInitialized.current){
            notificationsInitialized.current=true
            return
        }

        unseen.slice(0,3).reverse().forEach(d=>{
            notification.open({
                message:(
                    <div style={{display:"flex",alignItems:"center",color:"#FFF"}}>
                        <span style={{fontWeight:900,width:100,marginRight:20}}>{formater(d.montant,0,d.devise)}</span>
                        <span>{getDisplayName(d.campagneExtraData)}</span>
                    </div>
                ),
                description:(
                    <span style={{color:"#FFF"}}>{d.commentaire}</span>
                ),
                duration:5,
                style:{
                    width:350,
                    backgroundColor:progressColor
                }
            })
        })
    }

    const donorsUrl=(page)=>{
        let url="/campagne/donors/"+campagneData.id+"?page="+page
        if(campagneTarget.initial.team){
            url+="&team="+campagneTarget.initial.team
        }
        return url
    }

    // Fusionne sans doublon : un nouveau don décale la pagination côté API,
    // une page suivante peut donc renvoyer des dons déjà affichés.
    const mergeDons=(head,tail)=>{
        const seen=new Set()
        return [...head,...tail].filter(d=>{
            const k=donKey(d)
            if(seen.has(k)){ return false }
            seen.add(k)
            return true
        })
    }

    // Rechargement live : seule la première page est relue, les pages déjà
    // chargées au défilement sont conservées derrière.
    const getDons=async ()=>{
        const res=await ApiService.get(donorsUrl(1))
        if(Array.isArray(res?.data))
        {
            setDons(prev=>mergeDons(res.data,prev))
            setStats(res.stats)
            if(nextPage.current===2){
                setHasMore(res.data.length>=DONORS_PER_PAGE)
            }

            showDonNotification(res.data)
        }

        setLoading(false)
    }

    const loadMore=async ()=>{
        if(loadingMoreRef.current){ return }
        loadingMoreRef.current=true
        setLoadingMore(true)
        try{
            const page=nextPage.current
            const res=await ApiService.get(donorsUrl(page))
            if(Array.isArray(res?.data)){
                res.data.forEach(d=>seenIds.current.add(String(d?._id || d?.id || "")))
                setDons(prev=>mergeDons(prev,res.data))
                nextPage.current=page+1
                setHasMore(res.data.length>=DONORS_PER_PAGE)
            }
        }catch(_){
            // on laisse hasMore intact : le prochain passage du sentinel réessaiera
        }finally{
            loadingMoreRef.current=false
            setLoadingMore(false)
        }
    }

    useSerializedRefresh(getDons, campagneData?._liveRevision)

    // Chargement de la page suivante quand le bas de la liste approche.
    // Relancé après chaque page tant que le sentinel reste visible
    // (liste trop courte pour remplir la zone de défilement).
    useEffect(()=>{
        if(loading || !hasMore || loadingMore){ return }
        const root=scrollRef.current
        const target=sentinelRef.current
        if(!root || !target){ return }
        if(typeof IntersectionObserver==="undefined"){
            loadMore()
            return
        }
        const observer=new IntersectionObserver((entries)=>{
            if(entries.some(e=>e.isIntersecting)){
                observer.disconnect()
                loadMore()
            }
        },{root,rootMargin:"0px 0px 400px 0px"})
        observer.observe(target)
        return ()=>observer.disconnect()
    },[loading,hasMore,loadingMore,dons.length])

    let isMobile=context.isMobile
    if(forceMobile){
        isMobile=true
    }

    if(loading){
        return null
    }

    return (
        <div style={{
            display: "flex",
            alignItems: "start",
            flexDirection: "column",
            width:"100%",
        }}>

            <span className="title"><Trans>{stats?.count?.total} don(s)</Trans></span>

            <div ref={scrollRef} style={{
                display: "flex",
                flexWrap: "wrap",
                columnGap: "5%",
                rowGap: 30,
                padding: "1rem",
                marginTop: "2rem",
                width: "100%",
                maxHeight: "70vh",
                overflowY:"scroll"
            }}>
                {dons.map((d,index)=>{
                    //console.log("d",d)
                    const _team=Array.isArray(campagneData?.teams)
                        ? campagneData.teams.find(t=>t.param===d.campagneExtraData?.team && !t.hidden)
                        : null

                    let amountTextMonth=false
                    let amountText=formater(d.montant,0,d.devise)
                    if(d.type==="ABONNEMENT" && d.montantPerMonth && (campagneData?.config?.productMonth || campagneData?.config?.monthlyAmountDisplay)){
                        amountTextMonth=true
                        amountText=t`${formater(d.montantPerMonth,0,d.devise)} /Mois`
                    }


                    return(
                        <div key={donKey(d) || index} style={{
                            display: "flex",
                            flexDirection: "column",
                            width: isMobile ? "100%" : "30%",
                            //border:"1px solid red"
                        }}>

                            <div style={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
                                <div style={{display:"flex",flexDirection:"column"}}>
                                    <span style={{
                                        fontWeight: 900,
                                        fontSize: "1.125rem",
                                        lineHeight: "1.75rem",
                                        textTransform: "capitalize"
                                    }}>{getDisplayName(d.campagneExtraData)}</span>
                                    <span className={"time-ago"} style={{opacity:hideDonDate?0:1}}><Trans><TimeAgo datetime={d.datetime}/></Trans></span>

                                    {_team && (
                                        <a href={context.org.BASE_WEB_URL + "/" + _team.param}
                                           style={{color: "inherit",fontSize:"0.75rem",lineHeight:"1rem"}}>{_team.label}</a>
                                    )}
                                </div>


                                {amountTextMonth?(
                                    <span className={"-text-primary"}
                                          style={{fontWeight: 900, fontSize: "1.3rem"}}>{amountText}</span>
                                ): (
                                    <span className={"-text-primary"}
                                          style={{
                                              fontWeight: 900,
                                              fontSize: "1.875rem",
                                              lineHeight: "2.25rem"
                                          }}>{amountText}</span>
                                )}
                            </div>

                            {d.commentaire && (
                                <span style={{marginTop:"0.5rem",fontSize:"0.875rem",lineHeight:"1.25rem"}}>{d.commentaire}</span>
                            )}
                        </div>
                    )
                })}

                {hasMore && (
                    <div ref={sentinelRef} style={{width:"100%",display:"flex",justifyContent:"center",minHeight:40}}>
                        {loadingMore && <Spin size="small"/>}
                    </div>
                )}
            </div>

        </div>
    )
}
